import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  Input,
  VStack,
  Heading,
  Text,
  Container,
  Card,
  HStack,
} from "@chakra-ui/react";

import { useAppDispatch, useAppSelector } from "../store/hooks";
import { login } from "../store/slices/authSlice";
import { toaster } from "../components/ui/toaster";

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!/^\S+@\S+$/.test(formData.email)) {
      toaster.create({
        title: "Invalid email",
        type: "error",
        duration: 3000,
      });
      return;
    }

    try {
      await dispatch(
        login({ email: formData.email, password: formData.password }),
      ).unwrap();
      toaster.create({
        title: "Login successful",
        type: "success",
        duration: 3000,
      });
      navigate("/dashboard");
    } catch (error) {
      toaster.create({
        title: "Login failed",
        description: error || "Invalid credentials",
        type: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Box className="min-h-screen flex items-center justify-center" bg="gray.50">
      <Container maxW="md" py={12}>
        <VStack mb={6} textAlign="center">
          <Heading size="xl">Welcome Back</Heading>
          <Text color="gray.600">
            Don't have an account?{" "}
            <Link to="/register" className="text-blue-500 hover:text-blue-600">
              Create account
            </Link>
          </Text>
        </VStack>

        <Card.Root>
          <Card.Body p={8}>
            <form onSubmit={handleSubmit}>
              <VStack gap={4}>
                <Box width="full">
                  <Text mb={2} fontWeight="medium">
                    Email
                  </Text>
                  <Input
                    type="email"
                    placeholder="you@pharmasee.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </Box>

                <Box width="full">
                  <Text mb={2} fontWeight="medium">
                    Password
                  </Text>
                  <Input
                    type="password"
                    placeholder="Your password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required
                  />
                </Box>

                <HStack width="full" justify="space-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.remember}
                      onChange={(e) =>
                        setFormData({ ...formData, remember: e.target.checked })
                      }
                      className="w-4 h-4"
                    />
                    <Text fontSize="sm"> Remember me</Text>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-500 hover:text-blue-600">
                    Forgot password?
                  </Link>
                </HStack>

                <Button
                  type="submit"
                  bgColor={"blue.600"}
                  width="full"
                  loading={loading}
                  mt={4}>
                  Sign In
                </Button>
              </VStack>
            </form>
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
}
